<template>
  <div
    class="cn-product-card group"
    itemscope
    itemprop="itemListElement"
    itemtype="http://schema.org/Product"
  >
    <FeatureLabelGroup :product="product" />
    <div class="cn-product-card__wrapper">
      <div class="cn-product-card__borders">
        <div class="cn-product-card__border" />
        <div class="cn-product-card__border" />
      </div>
      <div class="cn-product-card__block">
        <div class="cn-product-card__image-wrapper">
          <CdnImg
            v-if="!product.variations"
            src="/svg/product-no-image.svg"
            loading="lazy"
            class="cn-product-card__image-placeholder"
            :width="317"
            :height="317"
          />
          <UiLink v-else :to="productSlug" class="block" itemprop="url">
            <CdnImg
              :key="productImage"
              :src="productImage ?? '/img/placeholder.png'"
              format="webp"
              loading="lazy"
              class="cn-product-card__image"
              :width="317"
              :height="317"
              itemprop="image"
            />
          </UiLink>
        </div>
        <div class="cn-product-card__content">
          <UiLink
            class="cn-product-card__name"
            :to="productSlug"
            itemprop="url"
          >
            <span itemprop="name">
              {{ product.name }}
            </span>
          </UiLink>
          <div class="cn-product-card__additional-info">
            <div class="cn-product-card__sku" itemprop="model">
              {{ productSKU }}
            </div>
            <div class="cn-product-card__colors">
              <UiColorSelect
                v-if="!similarProducts"
                :items="variations"
                @input="setVariation"
              />
              <UiColorSelect
                v-else
                :active-similar-product="activeSimilarProduct"
                :items="similarProducts"
                @input="setSimilarProduct"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useSimilarProducts } from '@/composables';
import type { Product as ProductType } from '@webplatform/frontkit/runtime/types';
import { CdnImg } from '@webplatform/cdn-image';
import UiColorSelect from '@/components/ui/UiColorSelect';
import UiLink from '@/components/ui/UiLink';

export type Product = ProductType & {
  image: string;
  status: number;
  external_cf_published_at: string;
};

interface Props {
  product: Product;
}

const props = defineProps<Props>();
const {
  currentVariation,
  variations,
  similarProducts,
  activeSimilarProduct,
  setVariation,
  setSimilarProduct,
  setProduct,
} = useSimilarProducts();
setProduct(props.product);

const productSlug = computed(
  () => activeSimilarProduct.value?.slug || `/product/${props.product.slug}`,
);

const productImage = computed(
  () =>
    activeSimilarProduct.value?.photo_url ||
    variations?.value?.[currentVariation.value]?.images?.[0],
);

const productSKU = computed(
  () =>
    activeSimilarProduct.value?.sku ||
    variations.value?.[currentVariation.value]?.sku,
);
</script>

<style lang="scss">
.cn-product-card {
  --ab-canyon-product-card-color-bg: var(--ab-canyon-color-neutral-light);
  --ab-canyon-product-card-borders-color-bg: var(--ab-canyon-color-accent-2);
  --ab-canyon-product-card-block-color-bg: var(--ab-canyon-color-light);
  --ab-canyon-product-card-name-color-text: var(--ab-canyon-color-dark);
  --ab-canyon-product-card-sku-color-text: var(--ab-canyon-color-neutral);

  background-color: var(--ab-canyon-product-card-color-bg);
  height: 100%;
  position: relative;

  &:hover {
    .cn-product-card {
      &__borders {
        @media (hover: hover) {
          opacity: 1;
          animation: spin 15s linear infinite;
        }
      }
      &__image {
        @media (hover: hover) {
          transform: scaleX(1.1) scaleY(1.1);
        }
      }
    }
  }
  &__wrapper {
    position: relative;
    padding: 2px;
    overflow: hidden;
    height: 100%;
  }
  &__borders {
    transition-duration: 500ms;
    transition-property: all;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    opacity: 0;
    width: 100%;
    height: 100%;
    position: absolute;
    div {
      background-color: var(--ab-canyon-product-card-borders-color-bg);
    }
  }
  &__border {
    width: 100%;
    height: 100%;
    &:nth-child(1) {
      transform: translate(50%, 50%);
    }
    &:nth-child(2) {
      transform: translate(-50%, -150%);
    }
  }
  &__block {
    background-color: var(--ab-canyon-product-card-block-color-bg);
    padding: var(--size-base);
    flex-direction: column;
    height: 100%;
    display: flex;
    z-index: 10;
    position: relative;
  }
  &__image-placeholder {
    width: 100%;
  }
  &__image {
    transition-duration: 500ms;
    transition-property: transform;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    width: 100%;
  }
  &__content {
    padding-top: var(--size-base);
    justify-content: space-between;
    flex-direction: column;
    flex-grow: 1;
    display: flex;
  }
  &__name {
    color: var(--ab-canyon-product-card-name-color-text);
    line-height: 1.25;
    display: block;
  }
  &__additional-info {
    column-gap: calc(var(--size-base) / 2);
    justify-content: space-between;
    align-items: flex-end;
    display: flex;
    margin-top: var(--size-xs);
  }
  &__sku {
    color: var(--ab-canyon-product-card-sku-color-text);
    font-size: 0.875rem;
    line-height: 1.25rem;
  }
  &__colors {
    padding-top: calc(var(--size-base) / 2);
    padding-bottom: calc(var(--size-base) / 2);
  }
}

@keyframes spin {
  100% {
    transform: rotate(360deg);
  }
}
</style>
